import React, { ChangeEvent, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Clear, ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  Link,
  OutlinedInput,
  Popover,
  Stack,
  Typography as T,
  useTheme,
} from "@mui/material";
import ConceptTypeBadge from "catalog/components/ConceptTypeBadge";
import useConceptSearch from "catalog/hooks/useConceptSearch";
import useConceptsLookup from "catalog/hooks/useConceptsLookup";
import useConceptsTree from "catalog/hooks/useConceptsTree";
import ConceptCategory, { DISPLAY_CONCEPT_CATEGORY } from "catalog/types/enums/conceptCategory";
import ConceptEntity, { DISPLAY_CONCEPT_ENTITY } from "catalog/types/enums/conceptEntity";
import { EntityInfo } from "catalog/types/interfaces/conceptResponse";
import { getTreeNodeById, mapIdToLink } from "catalog/utils/concept";
import pluralize from "pluralize";
import { ArrayParam, StringParam, useQueryParam, withDefault } from "use-query-params";
import { FilterIcon, SearchIcon } from "~/assets/icons";
import { TitleBreadcrumbs } from "~/components/AppHeader";
import EmptyState from "~/components/EmptyState";
import StickyToolbar from "~/components/StickyToolbar";
import useAppHeader from "~/contexts/header";
import { useDebounce } from "~/hooks/useDebounce";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { trackEvent } from "~/utils/monitoring";

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });
const FILTER_OPTIONS = [
  ConceptEntity.SITE,
  ConceptEntity.DERIVED_GROUP,
  ConceptEntity.QUESTION_GROUP,
];

const ConceptTypeFilter = () => {
  const { palette } = useTheme();
  const [filters, setFilters] = useQueryParam("filters", withDefault(ArrayParam, []));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedOption = e.target.name as ConceptEntity;

    if (e.target.checked) {
      setFilters([...filters, selectedOption]);
    } else {
      setFilters(filters.filter((val) => val !== selectedOption));
    }
  };

  return (
    <>
      <Badge
        badgeContent={filters.length || null}
        color="primary"
        sx={{
          "& > span": {
            fontSize: 10,
            top: 2,
            height: 16,
            minWidth: 16,
            fontWeight: 500,
            outline: `3px solid ${palette.common.pageBackground}`,
            userSelect: "none",
          },
        }}
      >
        <Button
          color="inherit"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ display: "flex", gap: 1 }}
        >
          <FilterIcon />
          Filter
        </Button>
      </Badge>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          elevation: 2,
          sx: {
            mt: 1,
            px: 2,
            py: 0.5,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {FILTER_OPTIONS.map((option) => (
          <FormControlLabel
            key={option}
            control={
              <Checkbox
                size="small"
                checked={(filters as ConceptEntity[]).includes(option)}
                name={option}
                onChange={handleFilterChange}
              />
            }
            label={<T variant="body2">{pluralize(DISPLAY_CONCEPT_ENTITY[option])}</T>}
          />
        ))}
      </Popover>
    </>
  );
};

const SearchResult = ({
  id,
  category,
  info,
}: {
  id: string;
  category: ConceptCategory;
  info: EntityInfo;
}) => {
  const { data: lookup } = useConceptsLookup();
  const { data: tree } = useConceptsTree();
  const entityLabel = DISPLAY_CONCEPT_ENTITY[info.type];

  const formattedCounts = info.counts
    ?.map(({ count, type }) => {
      const name = DISPLAY_CONCEPT_ENTITY[type];
      if (name) {
        return `${numFormatter.format(count)} ${pluralize(name, count)}`;
      }
    })
    .filter(Boolean)
    .join(" · ");

  const treeNode = getTreeNodeById(id, tree?.data);
  const breadcrumbs = lookup ? treeNode?.parentIds?.map((id) => mapIdToLink(id, lookup)) : [];

  return (
    <Box data-testid="SearchResult">
      <Box mb={0.5}>
        <ConceptTypeBadge category={category} label={entityLabel} />
      </Box>

      <Link
        component={RouterLink}
        underline="hover"
        to={`/data-browser/dashboards/concepts/${id}/details`}
        // TODO: Move this to theme?
        color="text.link"
        sx={{ display: "inline-block" }}
      >
        <T variant="body1" fontWeight={600} color="inherit" fontSize={18}>
          {info.name}
        </T>
      </Link>
      <Stack sx={{ opacity: 0.7, gap: 0.25 }}>
        {info.description && <T variant="body2">{info.description}</T>}
        {formattedCounts && (
          <T variant="body2" sx={{ textTransform: "lowercase" }}>
            {formattedCounts}
          </T>
        )}
        {breadcrumbs.length > 1 && (
          <T variant="body2">{breadcrumbs.map((crumb) => crumb?.label).join(" / ")}</T>
        )}
      </Stack>
    </Box>
  );
};

const title = "Search";

const ConceptSearch = () => {
  useDocumentTitle([title, "Data Browser"].join(" · "));
  const location = useLocation();
  useAppHeader({
    title: (
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Dashboards", href: "/data-browser/dashboards" },
          { title, href: location.pathname },
        ]}
      />
    ),
  });
  const [query, setQuery] = useQueryParam("q", withDefault(StringParam, ""));
  const { data, isLoading, error } = useConceptSearch();
  const debouncedQuery = useDebounce(query, 1200);

  useEffect(() => {
    trackEvent("catalog_searched", { query: debouncedQuery, entity_type: "concept" });
  }, [debouncedQuery]);

  return (
    <>
      <StickyToolbar>
        <Container
          maxWidth="xl"
          disableGutters
          sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative", px: 6 }}
        >
          <OutlinedInput
            fullWidth
            sx={{ maxWidth: 300 }}
            size="small"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            startAdornment={
              <Box display="flex" alignItems="center" justifyContent="center" pl={0.5}>
                <SearchIcon width={16} />
              </Box>
            }
            endAdornment={
              <Box display="flex" alignItems="center" gap={0.25}>
                {isLoading && (
                  <CircularProgress
                    variant="indeterminate"
                    disableShrink
                    thickness={4}
                    size={16}
                    sx={{
                      animationDuration: "550ms",
                    }}
                  />
                )}
                {query && (
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Clear search"
                    onMouseDown={(e) => {
                      // Prevent input blur (alongside using onMouseDown instead of onClick)
                      e.preventDefault();

                      setQuery("");
                    }}
                    sx={{ fontSize: 20, p: 0.25 }}
                  >
                    <Clear fontSize="inherit" />
                  </IconButton>
                )}
              </Box>
            }
          />
          <ConceptTypeFilter />
        </Container>
      </StickyToolbar>
      <Container maxWidth="xl" disableGutters sx={{ pl: 6 }}>
        <Box pb={2}>
          <Stack gap={3} py={2}>
            {error && (
              <Alert severity="error" variant="outlined">
                Unable to retrieve search results
              </Alert>
            )}
            {!isLoading && !data?.length && <EmptyState />}
            {data?.map(({ id, category, info }) => (
              <SearchResult key={id} id={id} category={category} info={info} />
            ))}
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default ConceptSearch;
